import React, { useEffect, useState } from 'react';
import { Save, X } from 'lucide-react';
import { formatPiiFoundDisplay, parsePiiFoundValue } from '../../services/cybersweep/piiFound';

interface ChecklistPiiMultiSelectProps {
  value: unknown;
  disabled?: boolean;
  options: string[];
  onSave: (selectedValues: string[]) => void;
}

export const ChecklistPiiMultiSelect: React.FC<ChecklistPiiMultiSelectProps> = ({
  value,
  disabled = false,
  options,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>(() => parsePiiFoundValue(value));

  useEffect(() => {
    if (!isEditing) {
      setSelected(parsePiiFoundValue(value));
    }
  }, [isEditing, value]);

  const displayValue = formatPiiFoundDisplay(value);

  const toggleValue = (option: string) => {
    setSelected((prev) =>
      prev.includes(option) ? prev.filter((entry) => entry !== option) : [...prev, option]
    );
  };

  if (isEditing) {
    return (
      <div className="relative">
        <div className="absolute left-0 top-0 z-20 w-64 rounded border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
          <div className="max-h-48 overflow-y-auto space-y-1">
            {options.map((option) => (
              <label
                key={option}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleValue(option)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-900 dark:text-gray-100">{option}</span>
              </label>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                onSave(selected);
                setIsEditing(false);
              }}
              className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
            >
              <Save className="h-3 w-3" />
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setSelected(parsePiiFoundValue(value));
                setIsEditing(false);
              }}
              className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <X className="h-3 w-3" />
              Cancel
            </button>
          </div>
        </div>
        <button
          type="button"
          className="w-full rounded border border-blue-500 bg-white px-2 py-1 text-left text-xs text-gray-700 dark:bg-gray-700/50 dark:text-gray-100"
        >
          {selected.length > 0 ? selected.join(', ') : 'Select PII...'}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      disabled={disabled}
      className="w-full rounded border border-gray-200 bg-gray-50 px-2 py-1 text-left text-xs text-gray-700 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-100"
    >
      {displayValue || 'Select PII...'}
    </button>
  );
};
